import { useEffect, useMemo, useState } from "react";
import { Search, X, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  EMPLOYER_MANUAL_SECTIONS,
  ADMIN_MANUAL_SECTIONS,
} from "@/lib/userManual";

// Fuzzy-ish search: lowercases, splits user query into tokens, requires every
// token to appear somewhere in the section's title or body. Highlights matches
// with <mark> in the rendered HTML.
function rankSections(sections, query) {
  const q = query.trim().toLowerCase();
  if (!q) return sections.map((s) => ({ section: s, hits: 0 }));
  const tokens = q.split(/\s+/).filter(Boolean);
  const ranked = [];
  for (const s of sections) {
    const haystack = (s.title + " " + s.body).toLowerCase();
    if (tokens.every((t) => haystack.includes(t))) {
      const titleHits = tokens.filter((t) =>
        s.title.toLowerCase().includes(t)
      ).length;
      ranked.push({ section: s, hits: titleHits * 5 + tokens.length });
    }
  }
  ranked.sort((a, b) => b.hits - a.hits);
  return ranked;
}

function highlight(html, query) {
  if (!query.trim()) return html;
  const tokens = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= 2);
  let out = html;
  for (const t of tokens) {
    // Escape regex special chars
    const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Match outside HTML tags only (rough — preserves embedded markup)
    out = out.replace(
      new RegExp(`(?![^<]*>)(${safe})`, "gi"),
      '<mark style="background:#fde68a;padding:0 2px;border-radius:2px;">$1</mark>'
    );
  }
  return out;
}

export default function ManualSearchModal({ open, onClose, kind = "employer" }) {
  const [query, setQuery] = useState("");
  const sections = kind === "admin" ? ADMIN_MANUAL_SECTIONS : EMPLOYER_MANUAL_SECTIONS;
  const ranked = useMemo(() => rankSections(sections, query), [sections, query]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="manual-search-modal"
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 p-4 pt-12 sm:pt-20"
      onClick={onClose}
    >
      <div
        className="flex h-[80vh] w-full max-w-3xl flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-3">
          <BookOpen className="h-4 w-4 text-blue-700" />
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
            {kind === "admin" ? "Admin Manual" : "Employer Manual"}
          </div>
          <button
            type="button"
            data-testid="manual-search-close"
            aria-label="Close"
            onClick={onClose}
            className="ml-auto rounded-full p-1 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="manual-search-input"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search the manual — try "stripe", "white-label email", "kanban"…'
              className="pl-9"
            />
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            {query.trim() ? (
              <>
                {ranked.length} match{ranked.length === 1 ? "" : "es"} ·{" "}
                <span className="text-slate-400">Esc to close</span>
              </>
            ) : (
              <>
                {sections.length} sections · type to search · Esc to close
              </>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {ranked.length === 0 ? (
            <p className="text-sm text-slate-500">
              No section matches "<strong>{query}</strong>". Try a shorter
              keyword or a different phrasing.
            </p>
          ) : (
            <div className="space-y-6">
              {ranked.map(({ section }, i) => (
                <article
                  key={section.title}
                  data-testid={`manual-result-${i}`}
                  className="border-b border-slate-100 pb-5 last:border-0"
                >
                  <h3
                    className="font-heading text-lg font-semibold text-slate-900"
                    dangerouslySetInnerHTML={{
                      __html: highlight(section.title, query),
                    }}
                  />
                  <div
                    className="prose prose-sm mt-2 max-w-none text-sm leading-relaxed text-slate-700 [&_code]:rounded-sm [&_code]:bg-slate-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[12px] [&_code]:text-slate-900 [&_li]:my-1 [&_p]:my-2"
                    dangerouslySetInnerHTML={{
                      __html: highlight(section.body, query),
                    }}
                  />
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
